import { createClient } from '@supabase/supabase-js';

// Helper function to log connection details in development mode only
const logConnectionDetails = (url: string, isDev: boolean) => {
  if (isDev) {
    console.log(
      `🔌 Admin Dashboard: Supabase connected to: ${url} [${import.meta.env.MODE} environment]`
    );
  }
};

// Initialize the Supabase client based on environment
const getSupabaseConfig = () => {
  const isDev = import.meta.env.MODE === 'development';

  // Use environment variables
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL ?? '';
  const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY ?? '';

  const isTest = import.meta.env.MODE === 'test';

  if (!supabaseUrl || !supabaseAnonKey) {
    if (isTest) {
      /*
       * During Vitest runs we allow missing credentials so that unit tests can
       * still import data-access helpers without providing real secrets.
       */
      return { supabaseUrl: 'https://test.supabase.local', supabaseAnonKey: 'anon' };
    }
    /*
     * Fail fast – trying to initialise Supabase without the correct
     * environment variables will cause hard-to-trace runtime errors in the
     * browser.  Throwing here surfaces the problem immediately during
     * development or at boot time in Cloudflare Pages Functions.
     */
    throw new Error(
      'Missing Supabase configuration.  Ensure VITE_SUPABASE_URL and ' +
        'VITE_SUPABASE_ANON_KEY are set in your environment.'
    );
  }

  // Log connection details in development
  logConnectionDetails(supabaseUrl, isDev);

  return { supabaseUrl, supabaseAnonKey };
};

const { supabaseUrl, supabaseAnonKey } = getSupabaseConfig();

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true
  },
  realtime: {
    params: {
      eventsPerSecond: 10
    }
  }
});

// Utility to help with authenticated storage operations
export const getAuthenticatedStorage = async () => {
  const isDev = import.meta.env.MODE === 'development';
  const { data } = await supabase.auth.getSession();
  const isAuthenticated = !!data?.session;

  // For development with auth bypass
  const authBypassEnabled =
    isDev && (localStorage.getItem('bypassAuth') === 'true');

  if (!isAuthenticated && authBypassEnabled) {
    console.warn(
      '⚠️ Storage operations may fail: Using auth bypass in development, but not authenticated for Supabase storage.'
    );
    console.warn(
      'ℹ️ Suggestion: Either log in as a user, or configure storage RLS policies to allow unauthenticated uploads.'
    );
  }

  return {
    storage: supabase.storage,
    isAuthenticated,
    authBypassEnabled,
    isDev,
  };
};

// Database Types based on existing schema
export interface Service {
  id: string;
  title: string;
  slug: string;
  description: string;
  full_description: string;
  content?: string; // Editor expects this field
  benefits: Array<{
    id: string;
    title: string;
    description: string;
  }>; // Editor expects objects, not strings
  process_steps: Array<{
    id: string;
    step_number: number;
    title: string;
    description: string;
  }>; // Editor expects id and step_number
  faq: Array<{
    question: string;
    answer: string;
  }>;
  faqs?: Array<{ // Editor uses 'faqs' not 'faq'
    id: string;
    question: string;
    answer: string;
  }>;
  image_url?: string;
  cta_text: string;
  cta_url?: string; // Editor expects this
  created_at: string;
  updated_at: string;
  meta_title?: string;
  meta_description?: string;
  og_title?: string;
  og_description?: string;
  og_image?: string;
  canonical_url?: string;
  focus_keyword?: string;
  status: 'draft' | 'published' | 'archived';
  is_featured: boolean;
  gallery_images?: {
    id: string;
    caption: string;
    image_url: string;
  }[];
  review_ids?: string[];
  hero_headline?: string;
  hero_image?: string;
  is_footer_service: boolean;
  schema_json?: any;
  // Additional fields expected by ServiceEditor
  service_areas?: string[];
  pricing_info?: string;
  category?: string;
  tags?: string[];
}

export interface Location {
  id: string;
  name: string;
  slug: string;
  parent_id?: string;
  description: string;
  meta_description?: string;
  is_primary: boolean;
  created_at: string;
  updated_at: string;
  meta_title?: string;
  og_title?: string;
  og_description?: string;
  og_image?: string;
  canonical_url?: string;
  focus_keyword?: string;
  status: 'published' | 'draft' | 'archived';
  area_description?: string;
  hero_image?: string;
  gallery_images?: string[];
  custom_title?: string;
  review_ids?: string[];
  schema_json?: any;
  // Address fields
  address_line1?: string;
  address_line2?: string;
  city?: string;
  province?: string;
  postal_code?: string;
  // Contact fields
  phone?: string;
  email?: string;
  hours?: string;
  // Service fields
  service_areas?: string[];
  services_offered?: Array<{
    id: string;
    service_name: string;
    description: string;
  }>;
  faqs?: Array<{
    id: string;
    question: string;
    answer: string;
  }>;
  // Franchise field
  franchise_region?: string;
}

export interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt?: string;
  content: string;
  featured_image?: string;
  author?: string; // Author name
  author_id?: string;
  category?: string;
  tags?: string[];
  meta_title?: string;
  meta_description?: string;
  status: 'draft' | 'published' | 'scheduled' | 'archived';
  published_at?: string;
  scheduled_for?: string;
  word_count?: number;
  reading_time?: number;
  created_at: string;
  updated_at: string;
}

export interface Gallery {
  id: number; // bigint in database
  title: string;
  description?: string;
  cover_image?: string;
  slug?: string;
  is_public: boolean;
  job_number?: number;
  review_status: 'pending' | 'approved' | 'rejected';
  service_id?: string;
  used_for_social_media?: boolean;
  franchise_region: 'HQ' | 'north_york' | 'london' | 'toronto' | 'hamilton' | 'mississauga' | 'brampton' | 'scarborough' | 'general';
  created_on: string; // actual field name in database
  approved_by?: string;
  approved_at?: string;
  rejection_reason?: string;
}

export interface GalleryPhoto {
  id: number; // bigint in database
  gallery_id: number; // bigint reference
  image_url: string;
  caption?: string;
  sort_order?: number;
  created_at: string;
}

// Convenience type used by selectors and hooks that fetch a gallery
// together with its photos for display/selection UIs.
export interface GalleryWithPhotos extends Gallery {
  gallery_photos: GalleryPhoto[];
}

export interface GalleryComplete {
  gallery_id: number;
  gallery_title: string;
  gallery_description?: string;
  cover_image?: string;
  franchise_region: 'HQ' | 'north_york' | 'london' | 'toronto' | 'hamilton' | 'mississauga' | 'brampton' | 'scarborough' | 'general';
  review_status: 'pending' | 'approved' | 'rejected';
  is_public: boolean;
  job_number?: number;
  created_on: string;
  services?: string[];
  tags?: string[];
}

export interface StaticPageRecord {
  id: string;
  title: string;
  slug: string;
  content: string;
  excerpt?: string;
  template: 'default' | 'about' | 'contact' | 'landing' | 'legal';
  status: 'draft' | 'published' | 'archived';
  menu_order?: number;
  is_homepage: boolean;
  show_in_nav: boolean;
  featured_image?: string | null;
  parent_id?: string | null;
  meta_title?: string | null;
  meta_description?: string | null;
  created_at: string;
  updated_at: string;
}

export interface ProjectRecord {
  id: string;
  title: string;
  slug: string;
  description: string;
  content?: string;
  featured_image?: string;
  gallery_images?: string[];
  service_id?: string;
  location_id?: string;
  client_name?: string;
  project_date?: string;
  project_duration?: string;
  project_value?: number;
  status: 'draft' | 'published' | 'featured' | 'archived';
  before_images?: string[];
  after_images?: string[];
  created_at: string;
  updated_at: string;
}

export interface SocialPost {
  id: string;
  profile_id?: string;
  content?: string;
  image_urls: string[];
  scheduled_time?: string;
  status: 'DRAFT' | 'SCHEDULED' | 'PUBLISHED' | 'FAILED';
  platform_specific_data?: any;
  created_at?: string;
  updated_at?: string;
  created_by?: string;
  queue_id?: string;
  queue_position?: number;
  caption?: string;
  media?: any;
  is_template?: boolean;
  tags?: string[];
  hashtags?: string[];
  mentions?: string[];
  time_slot?: string;
  published_at?: string;
  error_message?: string;
  last_status_change?: string;
  platform_response?: any;
  processing_logs?: string[];
}

export interface SocialProfile {
  id: string;
  name: string;
  credentials?: any;
  settings?: any;
  created_at?: string;
  updated_at?: string;
  platform: 'INSTAGRAM' | 'FACEBOOK' | 'TWITTER' | 'LINKEDIN';
  franchise_region: string;
  account_id?: string;
}

export interface Lead {
  id: string;
  client_name: string;
  client_email: string;
  client_phone?: string;
  service_street?: string;
  message?: string;
  service_requested?: string;
  service_slug?: string;
  location?: string;
  submission_url?: string;
  form_id?: string;
  form_name?: string;
  submission_status?: string;
  status: 'new' | 'contacted' | 'qualified' | 'quoted' | 'won' | 'lost';
  file_uploads?: any;
  created_at: string;
  updated_at: string;
}

export interface StaticPage {
  id: string;
  title: string;
  slug: string;
  content: string;
  excerpt?: string;
  template: 'default' | 'about' | 'contact' | 'landing' | 'legal';
  featured_image?: string;
  status: 'draft' | 'published' | 'archived';
  parent_id?: string;
  menu_order: number;
  is_homepage: boolean;
  show_in_nav: boolean;
  custom_fields?: Record<string, any>;
  created_at: string;
  updated_at: string;
}

export interface Profile {
  id: string;
  role: 'admin' | 'user';
  created_at: string;
  updated_at: string;
}

export interface GalleryTag {
  id: string;
  name: string;
  slug: string;
  description?: string;
  created_at: string;
  updated_at?: string;
}

export interface GalleryTagRelation {
  gallery_id: number;
  tag_id: string;
  created_at?: string;
}

// Franchise Images (Job Images) from external job management system
export interface FranchiseImageJob {
  id: number;
  job_id: number;
  job_number?: number;
  image_url: string;
  file_name?: string;
  franchise_region: 'HQ' | 'north_york' | 'london' | 'toronto' | 'hamilton' | 'mississauga' | 'brampton' | 'scarborough' | 'general';
  review_status: 'pending' | 'under_review' | 'approved' | 'rejected';
  status?: string;
  created_on: string;
  jobber_web_uri?: string;
  approved_by?: string;
  approved_at?: string;
  rejection_reason?: string;
}

// Bridge table linking franchise images to galleries
export interface GalleryJobImage {
  id: number;
  gallery_id: number;
  job_id: number;
  is_included: boolean;
  is_cover: boolean;
  sort_order?: number;
  created_at: string;
}

// Unified interface for all gallery images (from gallery_all_images view)
export interface GalleryAllImage {
  image_type: 'gallery_photo' | 'job_image';
  original_id: number; // ID from source table (gallery_photos.id or gallery_job_images.id)
  gallery_id: number;
  image_url: string;
  caption: string | null;
  review_status: 'pending' | 'under_review' | 'approved' | 'rejected';
  created_at: string;
  
  // Job image specific fields (only present when image_type === 'job_image')
  job_id?: string;  // Base64-encoded Jobber GraphQL ID (e.g., "Z2lkOi8vSm9iYmVyL0pvYi85OTU4OTE0Mg==")
  job_number?: number;
  franchise_region?: string;
  is_cover?: boolean;
  // Whether the job image is included in the gallery display (from gallery_job_images)
  is_included?: boolean;
  sort_order?: number;
  file_name?: string;
  jobber_web_uri?: string;
}

// (Removed duplicate Service interface to avoid TypeScript interface merging confusion)

export interface JobberProfile {
  id: string;
  franchise_region: string;
  jobber_token?: string;
  created_at: string;
  updated_at: string;
}
